import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EstadoService } from '../../../../services/configuracoes/estado.service';
import { Pais } from '../../../../enums/pais.enum';
import {
  EstadoDTO,
  EstadoFormDTO,
} from '../../../../models/configuracoes/estado.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-configuracao-estado-edit',
  templateUrl: './configuracao-estado-edit.component.html',
  styleUrls: ['./configuracao-estado-edit.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class ConfiguracaoEstadoEditComponent implements OnInit, OnDestroy {
  paises: { label: string; value: Pais; flagUrl: string }[] = [];
  selectedPais: { label: string; value: Pais } | undefined;
  form: EstadoFormDTO = this.createEmptyForm();

  texts = { titulo: '' };
  labels = { id: '', nome: '', codigo_fatura_eletronica: '', pais: '' };
  placeholders = { nome_do_estado: '', codigo_fatura_eletronica: '', selecione: '' };
  botoes = { atualizar: '' };

  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private estadoService: EstadoService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    this.paises = this.getPaises();

    // Captura o ID da rota e busca os dados do estado, se existir
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.estadoService.getById('/estado/' + id).subscribe((response) => {
        // Encontra o país correspondente na lista de opções
        const pais = this.paises.find(
          (p) => p.value === (response.pais as Pais)
        );

        this.form = {
          id: response.id,
          nome: response.nome,
          codigoFaturaEletronica: response.codigoFaturaEletronica,
          pais: pais ?? undefined, // Usa a mesma referência do objeto da lista para o PrimeNG encontrar o match
        };
      });
    }
  }

  getPaises(): { label: string; value: Pais; flagUrl: string }[] {
    return Object.values(Pais).map((pais) => ({
      label: String(pais).toLocaleUpperCase(),
      value: pais as Pais,
      flagUrl: this.getPaisFlagUrl(pais),
    }));
  }

  getPaisFlagUrl(pais: Pais): string {
    const paisStr = String(pais || '').toUpperCase();
    switch (paisStr) {
      case 'PARAGUAY':       return 'assets/images/paraguay-flag.jpg';
      case 'BRASIL':         return 'assets/images/br-flag.svg';
      case 'ARGENTINA':      return 'assets/images/argentina-flag.svg';
      case 'CHILE':          return 'assets/images/chile-flag.svg';
      case 'URUGUAY':        return 'assets/images/uruguay-flag.svg';
      case 'ESTADOS_UNIDOS': return 'assets/images/usa-flag.svg';
      default:               return '';
    }
  }

  submit() {
    if (this.form.nome === '') {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('estado-edit.mensagens.nome_obrigatorio'),
      });
      return;
    }
    if (this.form.codigoFaturaEletronica === '') {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('estado-edit.mensagens.codigo_obrigatorio'),
      });
      return;
    }
    if (this.form.pais === undefined) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('estado-edit.mensagens.pais_obrigatorio'),
      });
      return;
    }

    const body: EstadoDTO = {
      id: this.form.id,
      nome: this.form.nome,
      codigoFaturaEletronica: this.form.codigoFaturaEletronica,
      pais: this.form.pais.value,
    };

    this.estadoService.put(`/estado/${body.id}`, body).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('estado-edit.mensagens.estado_editado'),
        });
        this.form = this.createEmptyForm();
        this.router.navigate(['/configuracao-estado']);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('estado-edit.mensagens.falha_salvar'),
        });
      },
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('configuracao-estado-edit.texts.editar_estados')
    };
    this.labels = {
      id: this.translate.instant('configuracao-estado-edit.labels.id'),
      nome: this.translate.instant('configuracao-estado-edit.labels.nome'),
      codigo_fatura_eletronica: this.translate.instant('configuracao-estado-edit.labels.codigo_fatura_eletronica'),
      pais: this.translate.instant('configuracao-estado-edit.labels.pais')
    };
    this.placeholders = {
      nome_do_estado: this.translate.instant('configuracao-estado-edit.placeholders.nome_do_estado'),
      codigo_fatura_eletronica: this.translate.instant('configuracao-estado-edit.placeholders.codigo_fatura_eletronica'),
      selecione: this.translate.instant('configuracao-estado-edit.placeholders.selecione')
    };
    this.botoes = {
      atualizar: this.translate.instant('configuracao-estado-edit.botoes.atualizar')
    };
  }

  createEmptyForm(): EstadoFormDTO {
    return {
      nome: '',
      codigoFaturaEletronica: '',
      pais: undefined,
    };
  }
}
